"use client"
import { getDepictProductsBySearch } from "@/actions/products/get-depict-products-by-search"
import IconClose from "@/app/(site)/_components/Atoms/Icons/IconClose"
import Text from "@/app/(site)/_components/Atoms/Text"
import ButtonPill from "@/app/(site)/_components/Molecules/ButtonPill"
import InputField from "@/app/(site)/_components/Molecules/TextField"
import { useAnimatedModule } from "@/app/(site)/_utils/_hooks/useAnimatedModule"
import { useDebounce } from "@/app/(site)/_utils/_hooks/useDebounce"
import { useDocumentKeydown } from "@/app/(site)/_utils/_hooks/useDocumentKeydown"
import { useDocumentOverflowControl } from "@/app/(site)/_utils/_hooks/useDocumentOverflowControl"
import { useEscClose } from "@/app/(site)/_utils/_hooks/useEscClose"
import { useLocaleFromPathname } from "@/app/(site)/_utils/_hooks/useLocaleFromPathname"
import { getLangCode } from "@/app/(site)/_utils/misc"
import useEcommerceStore from "@/app/(site)/contexts/ecommerce/store"
import { transformDepictDisplayResponse } from "@/lib/depict-client/helpers/transformDepictResponse"
import useDepictClientMarketAndLocaleCode from "@/lib/depict-client/helpers/useDepictClientMarketAndLocaleCode"
import useDepictClientSessionId from "@/lib/depict-client/helpers/useDepictClientSessionId"
import { modalEvent, viewItemListEvent } from "@/lib/utils/tracking/gtmEvents"
import { adaptProductForTracking } from "@/lib/utils/tracking/itemDataTransformer"
import { useSiteContext } from "@/lib/utils/tracking/siteContext"
import { t } from "@/translations"
import { MinimizedCentraProduct } from "@/types/centra/checkout-api/products/post-products"
import React, { useEffect, useRef } from "react"
import { Spinner } from "../../Molecules/Spinner"
import RegularProductCard from "../../Organisms/Cards/ProductCards/RegularProductCard"

export type SearchModalProps = {
    locale: string
}

// Function to get the amount of grouped products
function amountOfGroupedProducts(
    groupedProducts: Record<string, MinimizedCentraProduct[]>
) {
    return Object.keys(groupedProducts).length
}

export default function SearchModal({ locale: propLocale }: SearchModalProps) {
    const NUMBER_OF_VARIANTS_IN_SEARCH = 80
    const DISPLAY_ITEMS_COUNT = true

    const [isAnimating, setIsAnimating] = React.useState(false)
    const [searchResult, setSearchResult] = React.useState<Record<
        string,
        MinimizedCentraProduct[]
    > | null>(null)

    const currentSearchPromise = React.useRef<Promise<void> | undefined>()
    const [searchResultCount, setSearchResultCount] = React.useState<number>(0)
    const [loading, setLoading] = React.useState(false)
    const locale = useLocaleFromPathname()

    const toggleModal = useEcommerceStore(state => state.toggleSearchModal)
    const isSearchModalOpen = useEcommerceStore(state => state.isSearchModalOpen)
    const depictSessionId = useDepictClientSessionId()

    const langCode = getLangCode(locale) ?? "en"
    const { marketCode, localeCode: depictLangCode } =
        useDepictClientMarketAndLocaleCode()

    const [searchTerm, setSearchTerm] = React.useState("")
    const centraSession = useEcommerceStore(state => state.centraSession)
    const siteContext = useSiteContext()

    // Tracking state management
    const hasTrackedOpen = useRef(false)
    const hasTrackedClose = useRef(false)
    const previousModalState = useRef(false)

    const searchProductsDebounce = useDebounce(async (searchTerm: string) => {
        if (!searchTerm) {
            setLoading(false)
            setSearchResult(null)
            setSearchResultCount(0)
            currentSearchPromise.current = undefined
            return
        }

        const searchPromise = getDepictProductsBySearch({
            market: marketCode,
            locale: depictLangCode,
            query: searchTerm,
            limit: NUMBER_OF_VARIANTS_IN_SEARCH,
            session_id: depictSessionId
        }).then(async response => {
            const groupedProducts = transformDepictDisplayResponse(
                response,
                "item_group_id"
            )

            // If we have made another search while searching, just abort and skip the rest.
            if (searchPromise !== currentSearchPromise.current) return

            // Early exit if we didn't get any result the first time, which means we should not.
            if (!groupedProducts?.length) {
                setLoading(false)
                setSearchResult({})
                setSearchResultCount(0)
                return
            }

            // Transform the array of objects to a Record
            const groupedProductsRecord: Record<string, MinimizedCentraProduct[]> =
                groupedProducts.reduce(
                    (acc, { key, items }) => {
                        acc[key] = items
                        return acc
                    },
                    {} as Record<string, MinimizedCentraProduct[]>
                )

            // If we have made another search while searching, just abort and skip the rest.
            if (searchPromise !== currentSearchPromise.current) return

            setLoading(false)

            setSearchResult(groupedProductsRecord)
            setSearchResultCount(Object.keys(groupedProductsRecord).length)

            // Track search results with proper data
            const searchResultsCount = Object.keys(groupedProductsRecord).length
            // Transform products for tracking (no categories for search)
            const transformedProducts = Object.values(groupedProductsRecord).map(
                (productGroup, index) => {
                    const product = productGroup[0] // Only the first variant per group
                    return {
                        ...adaptProductForTracking(product, {
                            item_list_name: "search",
                            item_list_id: `Search: ${searchTerm.toLowerCase()}`,
                            index
                        })
                    }
                }
            )

            // Track view item list event for search results
            viewItemListEvent(
                transformedProducts,
                "Search Results",
                `search-${searchTerm}`,
                siteContext,
                "search",
                `Search: ${searchTerm.toLowerCase()}`,
                undefined, // listSortOrder
                searchTerm.toLowerCase(),
                searchResultsCount
            )
        })

        setLoading(true)
        currentSearchPromise.current = searchPromise
    }, 300)

    const handleModuleClose = () => {
        // Track modal close event only once per cycle
        if (!hasTrackedClose.current) {
            modalEvent("search-modal", "Search Modal", "close", siteContext)
            hasTrackedClose.current = true
        }

        // Start closing animation
        setIsAnimating(false)

        setTimeout(() => {
            toggleModal(false)
        }, 300) // Adjust the duration to match your CSS animation
    }

    useAnimatedModule(isSearchModalOpen, setIsAnimating)
    useDocumentOverflowControl(isSearchModalOpen) // Controls the overflow of the body based on the modal state
    useEscClose(isSearchModalOpen, handleModuleClose) // Closes the modal on Escape key
    useDocumentKeydown({
        keys: ["Escape"], // Listens for Escape and Backspace keys
        action: handleModuleClose, // Calls handleModuleClose on key press
        condition: isSearchModalOpen // Only active when the modal is shown
    })

    // Modal tracking with proper state management for multiple cycles
    useEffect(() => {
        // Only track when modal state actually changes
        if (isSearchModalOpen !== previousModalState.current) {
            if (isSearchModalOpen && !hasTrackedOpen.current) {
                // Modal just opened - track open event
                modalEvent("search-modal", "Search Modal", "open", siteContext)
                hasTrackedOpen.current = true
                hasTrackedClose.current = false // Reset close tracking for this cycle
            } else if (!isSearchModalOpen) {
                // Modal just closed - reset tracking state for next cycle
                hasTrackedOpen.current = false
                hasTrackedClose.current = false
            }

            previousModalState.current = isSearchModalOpen
        }
    }, [isSearchModalOpen, siteContext])

    if (!isSearchModalOpen) return null

    // Conditional classes based on animation state
    const modalClasses = isAnimating ? "translate-x-0" : "translate-x-full" // Slide in from right

    const blurClasses = isAnimating ? "opacity-100" : "opacity-100"

    return (
        <div>
            <button
                className={`fixed left-0 top-0 z-30 h-full w-[100%] bg-black/20 !text-transparent backdrop-blur transition-all duration-300 ease-in-out ${blurClasses}`}
                onClick={() => handleModuleClose()}
                style={{ opacity: isAnimating ? 1 : 0 }}
                aria-label={t(langCode, "header.close_search_overlay")}
            >
                {t(langCode, "header.close_search_overlay")}
            </button>

            <div
                className={`fixed right-0 top-0 z-50 h-full w-full bg-white transition-transform duration-300 md:w-750 ${modalClasses}`}
            >
                <div className="relative flex h-[100%] flex-col justify-between px-24 py-32">
                    <div>
                        <button
                            className="absolute right-30 top-20 cursor-pointer"
                            aria-label={t(langCode, "header.close_search_overlay")}
                            onClick={handleModuleClose}
                        >
                            <IconClose className="h-30" />
                        </button>
                        <form
                            className="mb-32 flex flex-col gap-16 text-stone-400"
                            onSubmit={e => e.preventDefault()}
                        >
                            <InputField
                                placeholder="Search..."
                                id="searchModalInput"
                                label={t(langCode, "common.search")}
                                type={"search"}
                                theme="light"
                                single="true"
                                labelClassName="!mb-20"
                                onChange={e => {
                                    setSearchTerm(e.target.value)
                                    searchProductsDebounce(e.target.value)
                                }}
                                autoFocus={true}
                            />
                        </form>
                    </div>

                    <div className="mb-16 flex justify-between">
                        {searchResult && searchResultCount === 0 && (
                            <Text size="small" weight="light">
                                {t(langCode, "header.no_search_result")}
                            </Text>
                        )}
                        {searchResult && searchResultCount > 0 && (
                            <>
                                <Text size="medium" weight="regular">
                                    {t(langCode, "common.products")}
                                </Text>

                                {amountOfGroupedProducts(searchResult) > 0 &&
                                    DISPLAY_ITEMS_COUNT && (
                                        <Text size="medium" weight="regular">
                                            {amountOfGroupedProducts(searchResult)}{" "}
                                            {t(langCode, "cart.items")}
                                        </Text>
                                    )}
                            </>
                        )}
                    </div>

                    <div className="relative flex grow flex-col overflow-y-auto pb-16">
                        {loading && (
                            <div className="flex h-full w-full items-center justify-center">
                                <Spinner classes="w-50 h-50" />
                            </div>
                        )}
                        {!loading && searchResult && searchResultCount > 0 && (
                            <>
                                <div className="grid grid-cols-2 gap-10">
                                    {Object.values(searchResult).map(
                                        (productGroup, index) => (
                                            <RegularProductCard
                                                key={index}
                                                productVariants={productGroup}
                                                listName="search"
                                                listId={`Search: ${searchTerm.toLowerCase()}`}
                                                listType="search"
                                                listHierarchy={`Search: ${searchTerm.toLowerCase()}`}
                                                index={index}
                                            />
                                        )
                                    )}
                                </div>
                                <div className="pointer-events-none sticky bottom-[-16px] left-0 block h-[48px] w-[100%] flex-shrink-0 bg-gradient-to-t from-white to-transparent" />
                            </>
                        )}
                    </div>
                    <div className="flex-end">
                        <ButtonPill
                            color="black"
                            variant="primary"
                            size="large"
                            className="mb-16 w-full md:mb-0"
                            onClick={handleModuleClose}
                            aria-label={t(langCode, "header.close_search_overlay")}
                        >
                            {t(langCode, "common.close")}
                        </ButtonPill>
                    </div>
                </div>
            </div>
        </div>
    )
}
